<template>
  <div class="form-wrapper-process">
    <PanelInput
      :model-value="form.fileName"
      label="Selected File"
      :disabled="true"
      :tooltip="tooltips.appGroup.selectAppGroup"
      style="padding-left: 1rem; padding-right: 1rem"
    />
    <hr class="section-divider" />

    <div class="section-block">
      <span class="form-label">Select Destination</span>
      <div class="radio-group">
        <label class="radio-label">
          <input
            type="radio"
            :checked="form.importOptions.createNew === true"
            @change="update('importOptions', { ...form.importOptions, createNew: true, existingAppGroupId: null })"
          />
          Create New App Group
        </label>
        <label class="radio-label">
          <input
            type="radio"
            :checked="form.importOptions.createNew === false"
            @change="update('importOptions', { ...form.importOptions, createNew: false })"
          />
          Import to Existing App Group
        </label>
      </div>
    </div>

    <template v-if="form.importOptions.createNew === true">
      <hr class="section-divider" />
      <PanelInput
        :model-value="form.name"
        label="App Group Name"
        placeholder="Enter name"
        :max-length="40"
        :show-info="false"
        style="padding-left: 1rem; padding-right: 1rem"
        @update:model-value="update('name', $event)"
      />
    </template>

    <template v-else-if="form.importOptions.createNew === false">
      <hr class="section-divider" />
      <PanelDropdown
        :model-value="form.importOptions.existingAppGroupId"
        label="App Group Name"
        :options="existingGroupOptions"
        placeholder="Select App Group"
        style="padding-left: 1rem; padding-right: 1rem"
        @update:model-value="update('importOptions', { ...form.importOptions, existingAppGroupId: $event })"
      />
    </template>

    <template v-if="form.importOptions.createNew === true || (form.importOptions.createNew === false && form.importOptions.existingAppGroupId)">
      <hr class="section-divider" />
      <div class="section-block">
        <div class="label-row">
          <span class="form-label">Rules to Be Uploaded</span>
          <i class="ag-icon ag-icon_table_info small info-icon" />
        </div>
        <div class="checkbox-list">
          <div class="form-row select-all-row">
            <Checkbox
              :modelValue="allSelected"
              :binary="true"
              :indeterminate="someSelected && !allSelected"
              @change="toggleSelectAll($event.target.checked)"
            />
            <label class="input-label select-all-label">Select All</label>
          </div>
          <div
            v-for="rule in ruleList"
            :key="rule.key"
            class="form-row rule-row"
            :class="{ 'row-disabled': rule.disabled }"
          >
            <Checkbox
              :modelValue="rule.disabled ? false : form.importOptions[rule.key]"
              :binary="true"
              :disabled="rule.disabled"
              @change="!rule.disabled && update('importOptions', { ...form.importOptions, [rule.key]: $event.target.checked })"
            />
            <label class="input-label">{{ rule.label }}</label>
          </div>
        </div>
      </div>
      <div v-if="notAllowedByTypeLabels.length" class="rule-info-message">
        <i class="ag-icon ag-icon_info_yellow small" style="color: #f59e0b; flex-shrink: 0;" />
        <span><strong>{{ notAllowedByTypeLabels.join(', ') }}</strong> cannot be saved for this App Group type.</span>
      </div>
      <div v-if="noDataLabels.length" class="rule-info-message">
        <i class="ag-icon ag-icon_info_yellow small" style="color: #f59e0b; flex-shrink: 0;" />
        <span>No data available for <strong>{{ noDataLabels.join(', ') }}</strong> in the imported file.</span>
      </div>
    </template>

    <div
      v-if="!form.importOptions.createNew && someSelected && selectedGroupName"
      class="file-type-warning"
    >
      <i class="ag-icon ag-icon_info_yellow small yellow-info" style="color: #f59e0b; flex-shrink: 0;" />
      <span>Selected items from <strong>{{ selectedGroupName }}</strong> will be imported. Existing rules in the target App Group will not be modified.</span>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, defineProps, defineEmits } from 'vue';
import { useStore } from '@/store';
import Checkbox from 'primevue/checkbox';
import PanelInput from './components/panelInput.vue';
import PanelDropdown from './components/panelDropdown.vue';
import tooltips from '@/assets/tooltips/en.json';

const store = useStore();

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:form']);

const update = (key, value) => {
  emit('update:form', { ...props.form, [key]: value });
};

const allowedCollectionsByType = {
  1:    ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  2:    ['Process', 'CodeLoad'],
  4:    ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  8:    ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  16:   ['Process'],
  32:   ['Process'],
  64:   ['Process'],
  128:  ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  256:  ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  512:  ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  1024: ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  2048: ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
  4096: ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'],
};

const collectionKeyMap = {
  includeProcess:        'Process',
  includeFileAccess:     'FileAccess',
  includeRegistryAccess: 'RegistryAccess',
  includeCodeLoad:       'CodeLoad',
  includeChildLaunch:    'ChildLaunch',
};

const BASE_RULES = [
  { key: 'includeProcess',        label: 'Process',         jsonKey: 'AppGroupProcess' },
  { key: 'includeFileAccess',     label: 'File Access',     jsonKey: 'AppGroupFileAccess' },
  { key: 'includeRegistryAccess', label: 'Registry Access', jsonKey: 'AppGroupRegistry' },
  { key: 'includeCodeLoad',       label: 'Code Load',       jsonKey: 'AppGroupCodeLoad' },
  { key: 'includeChildLaunch',    label: 'Child Launch',    jsonKey: 'AppGroupChildLaunch' },
];

const targetAppGroupType = computed(() => {
  if (props.form.importOptions.createNew) {
    return props.form.importedData?.AppGroupType ?? 1;
  }
  const group = (store.getters.appGroups || []).find(
    (g) => (g.id ?? g.Id) == props.form.importOptions.existingAppGroupId
  );
  return group?.appGroupType ?? group?.AppGroupType ?? group?.type ?? group?.groupType ?? 1;
});

const allowedCollections = computed(() => {
  return allowedCollectionsByType[targetAppGroupType.value]
    ?? ['Process', 'FileAccess', 'RegistryAccess', 'CodeLoad', 'ChildLaunch'];
});

const ruleList = computed(() => {
  const data = props.form.importedData;
  return BASE_RULES.map((rule) => {
    const collectionName = collectionKeyMap[rule.key];
    const allowedByType = allowedCollections.value.includes(collectionName);
    const hasDataInFile = !!(data && data[rule.jsonKey] && data[rule.jsonKey].length > 0);
    return {
      ...rule,
      allowedByType,
      disabledByData: !hasDataInFile,
      disabled: !allowedByType || !hasDataInFile,
    };
  });
});

const notAllowedByTypeLabels = computed(() =>
  ruleList.value.filter((r) => !r.allowedByType).map((r) => r.label)
);

const noDataLabels = computed(() =>
  ruleList.value.filter((r) => r.allowedByType && r.disabledByData).map((r) => r.label)
);

watch(allowedCollections, (newAllowed) => {
  const updated = { ...props.form.importOptions };
  let changed = false;
  const data = props.form.importedData;

  for (const [optionKey, collectionName] of Object.entries(collectionKeyMap)) {
    if (!newAllowed.includes(collectionName) && updated[optionKey]) {
      updated[optionKey] = false;
      changed = true;
    } else if (newAllowed.includes(collectionName) && !updated[optionKey]) {
      const rule = BASE_RULES.find(r => r.key === optionKey);
      const hasData = !!(data && rule && data[rule.jsonKey] && data[rule.jsonKey].length > 0);
      if (hasData) {
        updated[optionKey] = true;
        changed = true;
      }
    }
  }

  if (changed) {
    update('importOptions', updated);
  }
});

const existingGroupOptions = computed(() =>
  (store.getters.appGroups || []).map((g) => ({
    label: g.name ?? g.Name,
    value: g.id ?? g.Id,
  }))
);

const existingGroups = computed(() =>
  (store.getters.appGroups || []).map((g) => ({
    id: g.id ?? g.Id,
    name: g.name ?? g.Name,
  }))
);

const enabledRuleKeys = computed(() =>
  ruleList.value.filter((r) => !r.disabled).map((r) => r.key)
);

const allSelected = computed(() =>
  enabledRuleKeys.value.length > 0 &&
  enabledRuleKeys.value.every((k) => props.form.importOptions[k])
);
const someSelected = computed(() =>
  enabledRuleKeys.value.some((k) => props.form.importOptions[k])
);

const selectedGroupName = computed(() => {
  if (!props.form.importOptions.existingAppGroupId) return '';
  return existingGroups.value.find(
    (g) => g.id == props.form.importOptions.existingAppGroupId
  )?.name || '';
});

const toggleSelectAll = (checked) => {
  const updated = { ...props.form.importOptions };
  enabledRuleKeys.value.forEach((k) => (updated[k] = checked));
  update('importOptions', updated);
};
</script>

<style scoped>
.section-divider {
  border: none;
  border-top: 1px solid #D2D9E2;
  margin: 0 1rem;
}

.form-wrapper-process {
  font-size: 0.8rem !important;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.form-label {
  color: #164b6c;
  font-weight: bold;
  margin-left: 8px;
}

.radio-group {
  display: flex;
  gap: 24px;
  padding-left: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  color: #01172f;
}

.label-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-icon {
  color: #9cb3c9;
}

.rule-row {
  padding-left: 20px;
}

.checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 8px;
}

.form-row {
  display: flex;
  align-items: center;
}

.input-label {
  margin-left: 10px;
  font-size: 0.758rem !important;
  cursor: pointer;
}

.select-all-label {
  font-weight: bold;
}

.select-all-row {
  padding-bottom: 6px;
}

.row-disabled {
  opacity: 0.4;
  pointer-events: none;
}

.rule-info-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #000000;
  background-color: #FFF9E7;
  border: 1px solid #FAC812;
  border-radius: 4px;
  padding: 8px 10px;
  margin: 0 1rem;
}

.file-type-warning {
  border: 1px solid #FAC812;
  padding: 8px 10px;
  background-color: #FFF9E7;
  color: #000000;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 1rem;
  margin-bottom: 1rem; 
}

::v-deep(.p-checkbox .p-checkbox-box) {
  background: transparent !important;
}

::v-deep(.p-checkbox.p-highlight .p-checkbox-box) {
  border-color: #3b82f6 !important;
  background: #3b82f6 !important;
}

::v-deep(.p-checkbox .p-checkbox-box) {
  width: 18px;
  height: 18px;
}

::v-deep(.p-checkbox .p-checkbox-box .p-checkbox-icon) {
  font-size: 12px;
}
</style>
